import { useAppDispatch, useAppSelector } from "@store/store";
import { selectClient } from "@store/client/clientReducer";
import { FunctionComponent, useCallback, useEffect } from "react";
import "./Menu.css";
import { colors } from "@ui-kit/ConfigProvider/ConfigProvider";
import { ExternalIcon } from "@icons/ExternalIcon";
import { Button } from "@ui-kit/Button";
import { LinkButton } from "@ui-kit/LinkButton/LinkButton";
import {
  selectIsUserAuthenticated,
  setIsAuthViewOpenAction,
  signOutAction,
} from "@store/sessionReducer";
import { TabButton } from "@ui-kit/TabButton/TabButton";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@hooks/useNavigate/useNavigate";
import { setMenuItemAction } from "@store/menuItem/menuItemReducer";
import { selectLastMenuItem } from "@store/menuItem/menuItemReducer";

interface MenuProps {
  isOpen: boolean;
  onClose: () => void;
}

const Menu: FunctionComponent<MenuProps> = ({ isOpen, onClose }) => {
  const client = useAppSelector(selectClient);
  const isUserAuthenticated = useAppSelector(selectIsUserAuthenticated);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lastMenuItem = useAppSelector(selectLastMenuItem);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const handleLogout = useCallback(() => {
    dispatch(signOutAction());
    onClose();
  }, [dispatch, onClose]);

  const handleExplore = useCallback(() => {
    navigate(lastMenuItem!.analyticsPath);
    dispatch(setMenuItemAction(lastMenuItem!));
    onClose();
  }, [dispatch, lastMenuItem, navigate, onClose]);

  const handleMyPlan = useCallback(() => {
    navigate("/plan");
    onClose();
  }, [navigate, onClose]);

  const handleSignIn = useCallback(() => {
    dispatch(setIsAuthViewOpenAction({ isAuthViewOpen: true }));
    onClose();
  }, [dispatch, onClose]);

  return (
    <>
      <div
        className={`menu-component-overlay ${isOpen ? "open" : ""}`}
        onClick={onClose}></div>
      <div
        className={`menu-component ${isOpen ? "open" : ""}`}
        style={{ backgroundColor: colors.primary60 }}>
        <div className="menu-header">
          <Button
            type="iconRound"
            icon={<ExternalIcon color={colors.white} svgName="close" />}
            onPress={onClose}
          />
        </div>
        <div className="logo-container">
          <img src={client.widgetLogoUrl} alt="Logo" height={40} />
        </div>
        <div className="menu-item-list">
          <div className="menu-item">
            <LinkButton
              color={colors.white}
              size="large"
              onPress={handleExplore}>
              {t("shell.explore")}
            </LinkButton>
          </div>
          <div className="menu-item">
            <LinkButton
              color={colors.white}
              size="large"
              onPress={handleMyPlan}>
              {t("shell.myPlan")}
            </LinkButton>
          </div>
        </div>
        <div className="menu-footer">
          {isUserAuthenticated && (
            <TabButton
              flex
              onPress={handleLogout}
              label={t("screens.account.logout")}
            />
          )}
          {!isUserAuthenticated && (
            <TabButton
              flex
              onPress={handleSignIn}
              label={t("authentication.signIn")}
            />
          )}
        </div>
      </div>
    </>
  );
};

export { Menu };
